import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { fetchAirReport, fetchAirReportDownloadLink, fetchReport } from './api'

export const reportKeys = {
	all: ['report'] as const,
	graylogReport: (tenant: string, start_time?: string, end_time?: string) =>
		[...reportKeys.all, tenant, start_time, end_time, 'no-cache'] as const,
	airReport: (tenant: string) => [...reportKeys.all, tenant, 'air'] as const,
	airDownloadLink: (tenant: string, report_name: string) =>
		[...reportKeys.all, tenant, report_name, 'air-download-link'] as const,
}

export const reportQueries = {
	graylogReport: (tenant: string, start_time?: string, end_time?: string) =>
		queryOptions({
			queryKey: reportKeys.graylogReport(tenant, start_time, end_time),
			queryFn: ({ signal }) =>
				fetchReport(tenant, start_time, end_time, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'd'),
		}),
	airReport: (tenant: string) =>
		queryOptions({
			queryKey: reportKeys.airReport(tenant),
			queryFn: ({ signal }) => fetchAirReport(tenant, signal),
			gcTime: time(1, 'h'),
			staleTime: ({ state }) => successStaleTime(state, 1, 'd'),
		}),
	airDownloadLink: (tenant: string, report_name: string) =>
		queryOptions({
			queryKey: reportKeys.airDownloadLink(tenant, report_name),
			queryFn: ({ signal }) =>
				fetchAirReportDownloadLink(tenant, report_name, signal),
			gcTime: time(5, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
		}),
}

/**
 * Custom hook to fetch Report
 * @param tenant - The tenant ID
 * @param start_time - The start time
 * @param end_time - The end time
 */
export const useGetReport = (
	tenant?: string,
	start_time?: string,
	end_time?: string,
) => {
	const currentTenant = useCurrentTenant()
	const selectedTenant = tenant ? tenant : currentTenant

	return useSuspenseQuery(
		reportQueries.graylogReport(selectedTenant, start_time, end_time),
	)
}

/**
 * Custom hook to fetch Air Report
 * @param tenant - The tenant ID
 */
export const useGetAirReport = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(reportQueries.airReport(tenant))
}

/**
 * Custom hook to fetch Download Link
 * @param report_name - The report name
 */
export const useGetAirReportDownloadLink = (report_name: string) => {
	const currentTenant = useCurrentTenant()

	return useSuspenseQuery(
		reportQueries.airDownloadLink(currentTenant, report_name),
	)
}
